import { createContext, use, useCallback } from 'react';

export type ReaderActionText = {
  word: string;
  passage: string;
  wordStart: number;
};

const readerActionTexts = new WeakMap<HTMLElement, ReaderActionText>();

export function registerReaderActionText(
  element: HTMLElement,
  text: ReaderActionText | undefined,
  unregister: () => void,
) {
  if (text) readerActionTexts.set(element, text);
  return () => {
    unregister();
    readerActionTexts.delete(element);
  };
}

export function readReaderActionText(element: HTMLElement) {
  const registered = readerActionTexts.get(element);
  if (registered) return registered;
  const text = element.textContent ?? '';
  const word = text.trim();
  const paragraph = element.closest('p');
  if (!paragraph) return { word, passage: word, wordStart: 0 };
  const prefix = element.ownerDocument.createRange();
  prefix.setStart(paragraph, 0);
  prefix.setEndBefore(element);
  return {
    word,
    passage: paragraph.textContent ?? word,
    wordStart: prefix.toString().length + text.length - text.trimStart().length,
  };
}

export const ReaderActionAnchorContext = createContext<{
  setAnchor: (element: HTMLElement | null) => void;
  clearAnchor: (element: HTMLElement) => void;
} | null>(null);

export function useReaderActionAnchorRef(active: boolean) {
  const anchor = use(ReaderActionAnchorContext);
  return useCallback(
    (element: HTMLElement | null) => {
      if (!active || !element || !anchor) return;
      anchor.setAnchor(element);
      return () => anchor.clearAnchor(element);
    },
    [active, anchor],
  );
}
